import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  TextField,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import StripeCheckout from './StripeCheckout';

const WhiteGloveService = ({ preferences, onClose }) => {
  const { user } = useAuth();
  const { createOneTimeCharge } = usePayment();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    location: ''
  });
  const [stripeCheckoutOpen, setStripeCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);

  const handleUserInfoChange = (field) => (event) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSendRequest = async () => {
    if (!user) {
      setError('Please log in to use this service');
      return;
    }

    // Validate required preferences fields
    if (!preferences.toLocation || !preferences.startDate || !preferences.endDate) {
      setError('Please fill in destination, start date, and end date in the preferences form');
      return;
    }

    // Validate user information with specific field checks
    const missingFields = [];
    if (!userInfo.name?.trim()) missingFields.push('Full Name');
    if (!userInfo.email?.trim()) missingFields.push('Email Address');
    if (!userInfo.phone?.trim()) missingFields.push('Phone Number');
    if (!userInfo.location?.trim()) missingFields.push('Location/City');

    if (missingFields.length > 0) {
      setError(`Please fill in all required contact information: ${missingFields.join(', ')}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userInfo.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create checkout session for $50 retainer fee using scalable function
      const session = await createOneTimeCharge(
        'white_glove_service',
        5000, // $50.00 in cents
        'White Glove Service - Retainer Fee',
        {
          user_email: userInfo.email,
          user_name: userInfo.name,
          user_phone: userInfo.phone,
          user_location: userInfo.location,
          preferences: JSON.stringify(preferences)
        }
      );

      if (session.clientSecret) {
        // Use embedded modal checkout
        setCheckoutData(session);
        setStripeCheckoutOpen(true);
      } else if (session.url) {
        // Fallback to redirect (shouldn't happen with embedded mode)
        window.location.href = session.url;
      } else {
        setError('Failed to create payment session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPreferences = () => {
    if (!preferences) return '';

    return `
**1. Basics:**
• From: ${preferences.fromLocation || 'Not specified'}
• To: ${preferences.toLocation || 'Not specified'}
• Start Date: ${preferences.startDate || 'Not specified'}
• End Date: ${preferences.endDate || 'Not specified'}
• Number of Travelers: ${preferences.travelers || 'Not specified'}
• Flexible Dates: ${preferences.flexibleDates ? 'Yes' : 'No'}

**2. Budget & Preferences:**
• Budget: ${preferences.budget || 'Not specified'}
• Budget Type: ${preferences.budgetType || 'Not specified'}
• Trip Types: ${preferences.tripTypes && preferences.tripTypes.length > 0 ? preferences.tripTypes.join(', ') : 'Not specified'}
• Preferred Pace: ${preferences.pace || 'Not specified'}

**3. Accommodation & Transport:**
• Accommodation Style: ${preferences.accommodationStyle || 'Not specified'}
• Transport Preferences: ${preferences.transportPreferences && preferences.transportPreferences.length > 0 ? preferences.transportPreferences.join(', ') : 'Not specified'}

**4. Restaurant Preferences:**
• Cuisine Types: ${preferences.restaurantPreferences?.cuisineTypes && preferences.restaurantPreferences.cuisineTypes.length > 0 ? preferences.restaurantPreferences.cuisineTypes.join(', ') : 'Not specified'}
• Dietary Restrictions: ${preferences.restaurantPreferences?.dietaryRestrictions && preferences.restaurantPreferences.dietaryRestrictions.length > 0 ? preferences.restaurantPreferences.dietaryRestrictions.join(', ') : 'Not specified'}
• Dining Style: ${preferences.restaurantPreferences?.diningStyle || 'Not specified'}
• Price Range: ${preferences.restaurantPreferences?.priceRange || 'Not specified'}
• Special Requests: ${preferences.restaurantPreferences?.specialRequests || 'Not specified'}

**5. Activities & Interests:**
• Interests: ${preferences.interests && preferences.interests.length > 0 ? preferences.interests.join(', ') : 'Not specified'}
• Must-See Items: ${preferences.mustSeeItems || 'Not specified'}
    `.trim();
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              White Glove Service
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Personalized travel planning by human experts
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Service Process:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              1. Select your trip preferences (completed)<br/>
              2. Pay $50 retainer fee (credited towards final booking)<br/>
              3. Our travel experts will build your custom itinerary<br/>
              4. Final fee of $350 to book all reservations and bookings
            </Typography>
          </Alert>

          {/* Contact Information Form */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ mr: 1, color: 'primary.main' }} />
                Contact Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please provide your contact information so our travel experts can reach you for consultation.
                <Typography component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}> *</Typography> All fields are required.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name *"
                    value={userInfo.name}
                    onChange={handleUserInfoChange('name')}
                    required
                    error={!userInfo.name?.trim()}
                    helperText={!userInfo.name?.trim() ? 'Full Name is required' : ''}
                    InputLabelProps={{
                      sx: { 
                        color: userInfo.name?.trim() ? 'text.primary' : 'error.main',
                        fontWeight: 'bold'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: userInfo.name?.trim() ? 'primary.main' : 'error.main',
                          borderWidth: userInfo.name?.trim() ? 1 : 2
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: userInfo.name?.trim() ? 'text.primary' : 'error.main' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address *"
                    type="email"
                    value={userInfo.email}
                    onChange={handleUserInfoChange('email')}
                    required
                    error={!userInfo.email?.trim()}
                    helperText={!userInfo.email?.trim() ? 'Email Address is required' : ''}
                    InputLabelProps={{
                      sx: { 
                        color: userInfo.email?.trim() ? 'text.primary' : 'error.main',
                        fontWeight: 'bold'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: userInfo.email?.trim() ? 'primary.main' : 'error.main',
                          borderWidth: userInfo.email?.trim() ? 1 : 2
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: userInfo.email?.trim() ? 'text.primary' : 'error.main' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    value={userInfo.phone}
                    onChange={handleUserInfoChange('phone')}
                    required
                    error={!userInfo.phone?.trim()}
                    helperText={!userInfo.phone?.trim() ? 'Phone Number is required' : ''}
                    placeholder="+1 (555) 123-4567"
                    InputLabelProps={{
                      sx: { 
                        color: userInfo.phone?.trim() ? 'text.primary' : 'error.main',
                        fontWeight: 'bold'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: userInfo.phone?.trim() ? 'primary.main' : 'error.main',
                          borderWidth: userInfo.phone?.trim() ? 1 : 2
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: userInfo.phone?.trim() ? 'text.primary' : 'error.main' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location/City *"
                    value={userInfo.location}
                    onChange={handleUserInfoChange('location')}
                    required
                    error={!userInfo.location?.trim()}
                    helperText={!userInfo.location?.trim() ? 'Location/City is required' : ''}
                    placeholder="e.g., New York, NY"
                    InputLabelProps={{
                      sx: { 
                        color: userInfo.location?.trim() ? 'text.primary' : 'error.main',
                        fontWeight: 'bold'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: userInfo.location?.trim() ? 'primary.main' : 'error.main',
                          borderWidth: userInfo.location?.trim() ? 1 : 2
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: userInfo.location?.trim() ? 'text.primary' : 'error.main' }} />
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stepper orientation="vertical" sx={{ mb: 3 }}>
            <Step active={true}>
              <StepLabel>Review Your Preferences</StepLabel>
              <StepContent>
                <Box sx={{ 
                  backgroundColor: '#f8f9fa', 
                  p: 3, 
                  borderRadius: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-line',
                  maxHeight: 400,
                  overflow: 'auto',
                  width: '100%'
                }}>
                  {formatPreferences()}
                </Box>
              </StepContent>
            </Step>
            
            <Step active={true}>
              <StepLabel>Pay Retainer Fee ($50)</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This $50 retainer fee will be credited towards your final booking fee of $350.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
                  onClick={handleSendRequest}
                  disabled={loading || 
                    !preferences.toLocation || 
                    !preferences.startDate || 
                    !preferences.endDate || 
                    !userInfo.name?.trim() || 
                    !userInfo.email?.trim() || 
                    !userInfo.phone?.trim() || 
                    !userInfo.location?.trim()}
                  sx={{ mb: 2 }}
                >
                  {loading ? 'Processing...' : 
                   (!preferences.toLocation || !preferences.startDate || !preferences.endDate) ? 
                   'Fill in destination and dates first' :
                   (!userInfo.name?.trim() || !userInfo.email?.trim() || !userInfo.phone?.trim() || !userInfo.location?.trim()) ?
                   'Complete all contact information first' :
                   'Pay $50 Retainer Fee'}
                </Button>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Expert Consultation</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  Our travel experts will contact you to build your custom itinerary and discuss your travel plans in detail.
                </Typography>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Final Booking ($350)</StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  Once your itinerary is finalized, we'll charge the remaining $300 to book all reservations and make necessary arrangements.
                </Typography>
              </StepContent>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Questions about our White Glove Service?
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              href="mailto:info@dejavunomad.com"
            >
              Contact Us
            </Button>
          </Box>

          {/* Stripe Checkout Modal */}
          {stripeCheckoutOpen && checkoutData && (
            <StripeCheckout
              open={stripeCheckoutOpen}
              onClose={() => {
                setStripeCheckoutOpen(false);
                setCheckoutData(null);
              }}
              clientSecret={checkoutData.clientSecret}
              tripTitle="White Glove Service"
              upgradeType="white_glove_service"
              quantity={1}
              onSuccess={() => {
                setStripeCheckoutOpen(false);
                setCheckoutData(null);
                // Close the entire White Glove Service component
                onClose();
              }}
            />
          )}
    </Box>
  );
};

export default WhiteGloveService;
